import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/honest-answers/website-cost");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/honest-answers/website-cost")({
  head: () => ({
    meta: [
        { name: "description", content: "Real pricing for a Colorado home-service website in 2026. DIY vs. freelancer vs. agency, what drives cost, and the trade-offs owners need to know." },
      {
        title: "What a Colorado Website Really Costs in 2026",
      },
      {
        name: "description",
        content:
          "Honest 2026 pricing breakdown for Colorado home-service websites — from $299 traps to $25K builds. What drives cost up, what drives it down, and what you should actually pay.",
      },
      {
        property: "og:title",
        content: "How much a Colorado home-service website really costs",
      },
      {
        property: "og:description",
        content:
          "Real ranges, what drives the price, and the cheap-website traps to avoid.",
      },
      { property: "og:url", content: `${SITE_URL}/honest-answers/website-cost` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/honest-answers/website-cost" }],
  }),
  component: WebsiteCostArticle,
});

function WebsiteCostArticle() {
  return (
    <ArticleLayout
      eyebrow="Pricing"
      read="9 min read"
      title="How much does a website really cost for a Colorado home-service business?"
      intro="Short answer: anywhere from $299 to $50,000. Useless answer, right? Let&rsquo;s actually break down what drives the number, what you should expect to pay in 2026, and where the traps are hiding."
      path="/honest-answers/website-cost"
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Honest Answers", to: "/honest-answers" },
        { label: "Real Website Cost" },
      ]}
      faqs={[
        { q: "How much does a small business website really cost in Colorado?", a: "A custom, lead-generating website for a Colorado service business typically runs $3,500–$12,000 one-time, or $150–$500/month if financed. Anything under $1,500 is usually a template with no strategy." },
        { q: "Why are some web design quotes 10x higher than others?", a: "Because 'website' means very different things. A 5-page brochure site is not the same product as a 30-page site with SEO foundations, conversion design, and ongoing optimization." },
        { q: "Do I own my website if I pay monthly?", a: "It depends on the agency. With us, yes — you own the code, content, and domain whether you pay one-time or monthly. Always ask before you sign." },
      ]}
    >
      <h2>The honest 2026 ranges</h2>
      <p>
        I&rsquo;ve been building websites for Colorado home-service companies since 2006. Here are
        the four buckets every quote you get will fall into:
      </p>

      <div className="not-prose my-8 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-destructive">
            $0 – $999 · The Trap
          </div>
          <div className="mt-2 text-lg font-bold text-foreground">
            DIY builders, $299 specials, &ldquo;free&rdquo; with hosting
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Wix, GoDaddy, $299 Fiverr templates, or the &ldquo;free website with hosting&rdquo;
            deals. You don&rsquo;t own anything, you can&rsquo;t move it, and Google can&rsquo;t
            find it. Fine for a hobby. Catastrophic for a business that needs leads.
          </p>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            $2,500 – $5,000 · Entry
          </div>
          <div className="mt-2 text-lg font-bold text-foreground">
            Solid 5–8 page custom site
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            A real designer, a real developer, mobile-responsive, basic on-page SEO, contact forms,
            tracking. This is the floor for anything that should generate leads. Most one-truck
            startups should live here.
          </p>
        </div>
        <div className="rounded-xl border border-brand-gold/40 bg-brand-gold/5 p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
            $5,000 – $12,000 · Sweet spot
          </div>
          <div className="mt-2 text-lg font-bold text-foreground">
            Lead-generation machine
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Custom design, 10–20 pages with service + city pages, real copywriting, schema,
            conversion-focused layouts, call tracking, online booking. Where most established
            Colorado home-service businesses should land.
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            $12,000 – $50,000+ · Enterprise
          </div>
          <div className="mt-2 text-lg font-bold text-foreground">
            Multi-location, custom integrations, deep SEO foundation
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            30+ pages, multi-city/multi-service architecture, CRM and dispatch integrations, custom
            booking flows, deep technical SEO. Only worth it if you&rsquo;re doing $2M+ in revenue
            or running multiple locations.
          </p>
        </div>
      </div>

      <h2>What actually drives the price</h2>
      <p>
        Two websites at the same price can deliver wildly different results. Here&rsquo;s where the
        money actually goes:
      </p>
      <ol>
        <li>
          <strong>Strategy &amp; copywriting</strong> — Most agencies skip this. It&rsquo;s also
          the single biggest driver of whether your site converts. Expect 20–40% of a real budget
          to go here.
        </li>
        <li>
          <strong>Custom design vs template</strong> — A custom layout built for your service area,
          your offer, and your customer adds $1,500–$4,000. It&rsquo;s usually worth it.
        </li>
        <li>
          <strong>Number of pages</strong> — Each city + service combo (&ldquo;HVAC repair in
          Monument&rdquo;) needs its own page if you want to rank. 10 pages vs 30 pages is real
          work.
        </li>
        <li>
          <strong>Photography &amp; video</strong> — Stock photos look like stock photos. Real
          photos of your team and trucks add $500–$2,000 and dramatically lift conversion.
        </li>
        <li>
          <strong>Integrations</strong> — Online booking, CRM, dispatch software, payment, review
          automation. Each one is real engineering time.
        </li>
        <li>
          <strong>Technical SEO foundation</strong> — Schema, sitemaps, page speed, internal
          linking, Core Web Vitals. Cheap sites skip all of it.
        </li>
      </ol>

      <h2>Where you should NOT spend money</h2>
      <ul>
        <li>
          <strong>Proprietary custom CMSes</strong> — Use a modern open framework (React/Next/TanStack) or a mainstream CMS. Bespoke one-off CMSes lock you in and cost a fortune to maintain.
        </li>
        <li>
          <strong>Animations and parallax everywhere</strong> — Looks impressive in a portfolio,
          slows your site, hurts conversion.
        </li>
        <li>
          <strong>30 stock photos</strong> — Three real ones beat 30 stock ones every time.
        </li>
        <li>
          <strong>&ldquo;Premium&rdquo; hosting at $200/mo for a small site</strong> — $30/mo is
          plenty for almost everyone reading this.
        </li>
      </ul>

      <h2>The cheap-website math (why $299 sites cost the most)</h2>
      <p>
        Here&rsquo;s the math we run for clients who got burned by a $299 deal:
      </p>
      <ul>
        <li>$299 site brings in maybe 1 lead/month. Most months: zero.</li>
        <li>A $5,000 site that converts properly: 8–15 leads/month.</li>
        <li>
          At a $400 average ticket and a 30% close rate, that&rsquo;s a difference of roughly{" "}
          <strong>$3,500–$6,000 a month</strong> in revenue.
        </li>
        <li>
          The $299 site doesn&rsquo;t cost you $299. It costs you the $5,000 site you should have
          bought, plus a year of missed revenue. Real cost: $40,000+.
        </li>
      </ul>

      <h2>What a fair Colorado quote looks like</h2>
      <p>
        For a typical home-service business in Colorado Springs, Denver, or anywhere along the
        Front Range, a fair quote in 2026 should include:
      </p>
      <ul>
        <li>10–15 pages with at least 3 service pages and 3 city/area pages</li>
        <li>Copywriting written for your offer, not stuffed by AI</li>
        <li>Schema markup, sitemap, and Search Console setup</li>
        <li>Click-to-call and form conversion tracking wired into Google Analytics 4</li>
        <li>Mobile-first design that loads in under 3 seconds on a phone</li>
        <li>Full ownership — your domain, your hosting account, your code</li>
      </ul>
      <p>
        Expect to pay <strong>$5,000–$10,000</strong> for that. Anyone quoting you under $2,500 is
        cutting something important. Anyone quoting over $15,000 better have a very good reason.
      </p>

      <h2>One last warning</h2>
      <p>
        If an agency won&rsquo;t put pricing on their website, hesitates to give you a written
        scope, or pushes you to sign a 12-month contract before showing you a draft — walk away.
        Those are the same patterns I&rsquo;ve seen burn Colorado business owners for 20 years.
      </p>
      <p>
        Our pricing is on{" "}
        <Link to="/pricing" className="font-bold text-brand-gold-deep underline">
          our pricing page
        </Link>
        . Three packages, real numbers, no email gate.
      </p>
    </ArticleLayout>
  );
}
